import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";

function renderBlock(block, key) {
  const lines = block.split("\n");
  const out = [];
  let bullets = [];
  let startIdx = 0;

  if (lines[0].startsWith("# ")) {
    out.push(
      <h2 key={`h-${key}`} className="font-serif font-medium tracking-tight text-2xl md:text-3xl text-charcoal pt-4">
        {lines[0].slice(2)}
      </h2>
    );
    startIdx = 1;
  }

  const flush = (k) => {
    if (!bullets.length) return;
    out.push(
      <ul key={k} className="list-disc pl-6 space-y-2 text-charcoal/75 marker:text-gold">
        {bullets.map((b, j) => (
          <li key={j}>{b}</li>
        ))}
      </ul>
    );
    bullets = [];
  };

  for (let i = startIdx; i < lines.length; i++) {
    const t = lines[i].trim();
    if (t.startsWith("- ")) {
      bullets.push(t.slice(2));
    } else {
      flush(`u${key}-${i}`);
      if (t) out.push(<p key={`p${key}-${i}`} className="text-charcoal/75">{t}</p>);
    }
  }
  flush(`u${key}-end`);

  return (
    <div key={key} className="space-y-3">
      {out}
    </div>
  );
}

export default function LegalPage({ pageKey, fallbackTitle }) {
  const [page, setPage] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    api
      .get("/content")
      .then((r) => {
        const p = r.data[pageKey] || { title: fallbackTitle, body: "" };
        setPage(p);
        document.title = `${p.title} — Kannur TheyyamNow`;
      })
      .catch(() => setPage({ title: fallbackTitle, body: "" }));
    const robots = document.createElement("meta");
    robots.setAttribute("name", "robots");
    robots.setAttribute("content", "noindex");
    robots.setAttribute("data-legal-page", "1");
    document.head.appendChild(robots);
    return () => robots.remove();
  }, [pageKey, fallbackTitle]);

  const blocks = (page?.body || "").split("\n\n").filter(Boolean);

  return (
    <div className="min-h-screen bg-ivory text-charcoal font-sans" data-testid={`legal-page-${pageKey}`}>
      <header className="border-b border-charcoal/10 bg-ivory/90 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-[72px] flex items-center justify-between">
          <Link to="/" data-testid="legal-logo" className="flex items-center">
            <img src="/logo.png" alt="Kannur TheyyamNow" className="h-12 w-auto" />
          </Link>
          <Link
            to="/"
            data-testid="legal-back-link"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-medium text-charcoal/70 hover:text-vermillion transition-colors duration-300"
          >
            <ArrowLeft size={15} /> Back to Home
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-20 md:py-28">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        >
          <p className="text-xs uppercase tracking-[0.35em] text-gold font-medium">Kannur TheyyamNow</p>
          <h1 className="mt-5 font-serif font-medium tracking-tight text-4xl md:text-6xl" data-testid="legal-title">
            {page?.title || fallbackTitle}
          </h1>
          <div className="mt-10 md:mt-14 space-y-8 text-base md:text-lg leading-relaxed" data-testid="legal-body">
            {page ? (
              blocks.length > 0 ? (
                blocks.map((b, i) => renderBlock(b, i))
              ) : (
                <p className="text-charcoal/40">Content coming soon.</p>
              )
            ) : (
              <p className="text-charcoal/40">Loading…</p>
            )}
          </div>
        </motion.div>
      </main>

      <footer className="border-t border-charcoal/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8 flex items-center justify-between">
          <img src="/logo.png" alt="Kannur TheyyamNow" className="h-9 w-auto" />
          <p className="text-xs text-charcoal/50">© Kannur TheyyamNow. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
